import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, Save, Loader2, AlertTriangle, Upload,
  Image as ImageIcon, Type, Link as LinkIcon, History,
  RotateCcw, X, Check, Eye, Sparkles, Pencil
} from 'lucide-react';
import { contentService } from '../../services/ContentService';

const PlacidContentEditor = () => {
  const { batchId, contentId } = useParams();
  
  // Refs
  const canvasRef = useRef(null);
  const canvasInstanceRef = useRef(null);
  const layersRef = useRef({});
  
  // ✅ Ref para controlar se o componente está montado (evita setState após unmount)
  const isMountedRef = useRef(true);
  // ✅ Ref para evitar inicialização dupla do canvas
  const isInitializingRef = useRef(false);
  // ✅ Ref para guardar timers pendentes e poder limpá-los
  const pendingTimersRef = useRef([]);
  
  // Estados
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [error, setError] = useState(null);
  const [saveSuccess, setSaveSuccess] = useState(false);
  const [sdkReady, setSdkReady] = useState(false);
  const [sdkError, setSdkError] = useState(null);
  
  // Dados
  const [content, setContent] = useState(null);
  const [canvasToken, setCanvasToken] = useState(null);
  const [canvasReady, setCanvasReady] = useState(false);
  
  // Layers
  const [layers, setLayers] = useState({});
  const [originalLayers, setOriginalLayers] = useState({});
  const [hasChanges, setHasChanges] = useState(false);
  
  // Histórico
  const [showHistory, setShowHistory] = useState(false);
  const [editHistory, setEditHistory] = useState([]);
  
  // Template
  const [templateUuid, setTemplateUuid] = useState(null);
  const [templateInfo, setTemplateInfo] = useState(null);

  // Manter ref sincronizada com state
  useEffect(() => {
    layersRef.current = layers;
  }, [layers]);

  // ============================================
  // ✅ CONTROLE DE MONTAGEM (CRÍTICO)
  // ============================================
  
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      
      // Limpar TODOS os timers pendentes
      pendingTimersRef.current.forEach(timer => clearTimeout(timer));
      pendingTimersRef.current = [];
      
      // Destruir canvas de forma segura
      destroyCanvas();
    };
  }, []);

  // ============================================
  // ✅ HELPER: setTimeout seguro (auto-limpa no unmount)
  // ============================================
  
  const safeTimeout = useCallback((fn, delay) => {
    const timer = setTimeout(() => {
      // Remover da lista de pendentes
      pendingTimersRef.current = pendingTimersRef.current.filter(t => t !== timer);
      // Só executar se ainda montado
      if (isMountedRef.current) {
        fn();
      }
    }, delay);
    pendingTimersRef.current.push(timer);
    return timer;
  }, []);

  // ============================================
  // ✅ HELPER: setState seguro (ignora se desmontado)
  // ============================================
  
  const safeSetState = useCallback((setter, value) => {
    if (isMountedRef.current) {
      setter(value);
    }
  }, []);

  // ============================================
  // ✅ DESTRUIR CANVAS DE FORMA SEGURA
  // ============================================
  
  const destroyCanvas = useCallback(() => {
    const instance = canvasInstanceRef.current;
    if (!instance) return;
    
    try {
      // Remover event listeners (se o SDK suportar .off)
      if (typeof instance.off === 'function') {
        instance.off('canvas:loaded');
        instance.off('canvas:error');
      }
      
      // Destruir instância
      if (typeof instance.destroy === 'function') {
        instance.destroy();
      }
    } catch (e) {
      console.warn('[PlacidEditor] Erro ao destruir canvas (ignorando):', e);
    }
    
    canvasInstanceRef.current = null;
    
    // ✅ Limpar o DOM do container do canvas para remover iframes/elementos residuais
    if (canvasRef.current) {
      canvasRef.current.innerHTML = '';
    }
  }, []);

  // ============================================
  // ✅ VERIFICAR SDK (já carregado via index.html)
  // ============================================
  
  useEffect(() => {
    // O SDK já está no index.html via <script src="https://sdk.placid.app/editor-sdk@latest/sdk.js">
    // Apenas verificar se está disponível, sem carregar novamente
    
    const checkSDK = () => {
      if (window.EditorSDK) {
        safeSetState(setSdkReady, true);
        return true;
      }
      return false;
    };
    
    // Verificar imediatamente
    if (checkSDK()) return;
    
    // Se não está pronto ainda, aguardar com polling limitado
    let attempts = 0;
    const maxAttempts = 30; // Máximo 3 segundos
    
    const interval = setInterval(() => {
      attempts++;
      if (checkSDK() || attempts >= maxAttempts) {
        clearInterval(interval);
        if (attempts >= maxAttempts && !window.EditorSDK) {
          safeSetState(setSdkError, 'Placid SDK não carregou. Verifique o script no index.html.');
        }
      }
    }, 100);
    
    return () => clearInterval(interval);
  }, [safeSetState]);

  // ============================================
  // CARREGAR DADOS
  // ============================================
  
  useEffect(() => {
    loadContentData();
  }, [batchId, contentId]);

  const loadContentData = async () => {
    if (!isMountedRef.current) return;
    
    setIsLoading(true);
    setError(null);
    
    try {
      const batchResponse = await contentService.getBatch(batchId);
      if (!isMountedRef.current) return; // Checagem após await
      
      const batchData = batchResponse.data;
      const contentData = batchData.contents?.find(c => c.id === contentId);
      
      if (!contentData) throw new Error('Conteúdo não encontrado');
      if (contentData.model !== 'placid') throw new Error('Edição disponível apenas para conteúdos Placid');
      if (contentData.status !== 'completed') throw new Error('Conteúdo ainda não foi processado');
      
      setContent(contentData);
      
      const uuid = contentData.metadata?.template_uuid;
      if (!uuid) throw new Error('Template UUID não encontrado');
      setTemplateUuid(uuid);
      
      const currentLayers = contentData.metadata?.layers || {};
      setLayers(currentLayers);
      layersRef.current = currentLayers;
      setOriginalLayers(JSON.parse(JSON.stringify(currentLayers)));
      
      setEditHistory(contentData.edit_history || []);
      setTemplateInfo({
        id: contentData.metadata?.template_id,
        title: contentData.metadata?.template_title
      });
      
      const tokenResponse = await contentService.getPlacidCanvasToken(uuid);
      if (!isMountedRef.current) return; // Checagem após await
      
      const token = tokenResponse.access_token || tokenResponse.data?.access_token;
      if (!token) throw new Error('Token não encontrado');
      
      setCanvasToken(token);
      
    } catch (err) {
      if (isMountedRef.current) {
        setError(err.message || 'Erro ao carregar');
      }
    } finally {
      if (isMountedRef.current) {
        setIsLoading(false);
      }
    }
  };

  // ============================================
  // ✅ INICIALIZAR CANVAS (com proteção contra dupla inicialização)
  // ============================================
  
  useEffect(() => {
    if (
      sdkReady && 
      canvasToken && 
      templateUuid && 
      canvasRef.current && 
      !canvasInstanceRef.current &&
      !isInitializingRef.current // ✅ Previne dupla inicialização
    ) {
      initializeCanvas();
    }
    
    // ✅ Cleanup: destruir canvas quando dependências mudarem ou componente desmontar
    return () => {
      destroyCanvas();
      safeSetState(setCanvasReady, false);
    };
  }, [sdkReady, canvasToken, templateUuid, destroyCanvas, safeSetState]);

  const initializeCanvas = () => {
    const SDK = window.EditorSDK;
    if (!SDK?.canvas) {
      safeSetState(setSdkError, 'EditorSDK.canvas não disponível');
      return;
    }
    
    // ✅ Prevenir dupla inicialização
    if (isInitializingRef.current || canvasInstanceRef.current) return;
    isInitializingRef.current = true;
    
    try {
      // ✅ Limpar container antes de criar nova instância
      if (canvasRef.current) {
        canvasRef.current.innerHTML = '';
      }
      
      const instance = SDK.canvas.create(canvasRef.current, {
        access_token: canvasToken,
        template_uuid: templateUuid
      });
      
      canvasInstanceRef.current = instance;
      
      // ✅ Handler com verificação de montagem
      const handleCanvasLoaded = () => {
        if (!isMountedRef.current) return;
        
        safeTimeout(() => {
          if (!isMountedRef.current || !canvasInstanceRef.current) return;
          
          const currentLayers = layersRef.current;
          if (Object.keys(currentLayers).length > 0) {
            try {
              canvasInstanceRef.current.fillLayers(currentLayers);
            } catch (e) {
              console.warn('[PlacidEditor] Erro ao preencher layers:', e);
            }
          }
          safeSetState(setCanvasReady, true);
        }, 300);
      };
      
      const handleCanvasError = (error) => {
        if (!isMountedRef.current) return;
        safeSetState(setSdkError, 'Erro no Canvas: ' + (error?.message || 'desconhecido'));
      };
      
      instance.on('canvas:loaded', handleCanvasLoaded);
      instance.on('canvas:error', handleCanvasError);
      
    } catch (err) {
      safeSetState(setSdkError, 'Erro ao inicializar: ' + err.message);
    } finally {
      isInitializingRef.current = false;
    }
  };

  // ============================================
  // ATUALIZAR CANVAS
  // ============================================
  
  const updateCanvas = useCallback((newLayers) => {
    if (canvasInstanceRef.current && canvasReady && isMountedRef.current) {
      try {
        canvasInstanceRef.current.fillLayers(newLayers);
      } catch (err) {
        console.warn('[PlacidEditor] Erro ao atualizar canvas:', err);
      }
    }
  }, [canvasReady]);

  // ============================================
  // HANDLERS
  // ============================================
  
  const handleTextChange = (layerName, value) => {
    const newLayers = {
      ...layers,
      [layerName]: { ...layers[layerName], text: value }
    };
    setLayers(newLayers);
    setHasChanges(JSON.stringify(newLayers) !== JSON.stringify(originalLayers));
    updateCanvas(newLayers);
  };
  
  const handleImageUrlChange = (layerName, url) => {
    const newLayers = {
      ...layers,
      [layerName]: { ...layers[layerName], image: url }
    };
    setLayers(newLayers);
    setHasChanges(true);
    updateCanvas(newLayers);
  };

  const handleImageUpload = async (layerName, file) => {
    if (!file) return;
    
    const allowedTypes = ['image/jpeg', 'image/png', 'image/webp', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      alert('Use: JPEG, PNG, WebP ou GIF');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      alert('Máximo: 10MB');
      return;
    }
    
    setIsUploading(true);
    try {
      const response = await contentService.uploadEditImage(contentId, file);
      if (!isMountedRef.current) return;
      
      const imageUrl = response.url || response.data?.url;
      if (imageUrl) handleImageUrlChange(layerName, imageUrl);
    } catch (err) {
      if (isMountedRef.current) alert('Erro no upload');
    } finally {
      if (isMountedRef.current) setIsUploading(false);
    }
  };

  const handleSave = async () => {
    if (!hasChanges) return;
    
    setIsSaving(true);
    setSaveSuccess(false);
    
    try {
      await contentService.saveContentEdit(batchId, contentId, {
        layers: layers,
        regenerate: true
      });
      
      if (!isMountedRef.current) return;
      
      setOriginalLayers(JSON.parse(JSON.stringify(layers)));
      setHasChanges(false);
      setSaveSuccess(true);
      await loadContentData();
      safeTimeout(() => setSaveSuccess(false), 3000);
    } catch (err) {
      if (isMountedRef.current) {
        alert('Erro ao salvar: ' + (err.message || 'Tente novamente'));
      }
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const handleRevert = async (historyIndex) => {
    if (!window.confirm('Reverter para esta versão?')) return;
    
    setIsSaving(true);
    try {
      await contentService.restoreContentVersion(batchId, contentId, historyIndex);
      if (!isMountedRef.current) return;
      await loadContentData();
      setShowHistory(false);
    } catch (err) {
      if (isMountedRef.current) alert('Erro ao reverter');
    } finally {
      if (isMountedRef.current) setIsSaving(false);
    }
  };

  const getLayerType = (layerName, layerValue) => {
    if (layerValue?.image !== undefined) return 'picture';
    if (layerValue?.text !== undefined) return 'text';
    return 'unknown';
  };

  const formatLayerName = (name) => {
    return name
      .replace(/-/g, ' ')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());
  };

  // ============================================
  // RENDER - LOADING
  // ============================================
  
  if (isLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-amber-500 animate-spin mx-auto mb-3" />
          <p className="text-gray-600">Carregando editor...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER - ERROR
  // ============================================
  
  if (error || sdkError) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md w-full text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-900 mb-2">Erro</h2>
          <p className="text-gray-600 mb-6">{error || sdkError}</p>
          <div className="flex gap-3 justify-center">
            <button
              onClick={() => window.location.reload()}
              className="px-5 py-2.5 bg-amber-500 text-white rounded-xl font-medium hover:bg-amber-600 transition-all"
            >
              Recarregar
            </button>
            <Link
              to={`/contents/batch/${batchId}`}
              className="px-5 py-2.5 bg-gray-100 text-gray-700 rounded-xl font-medium hover:bg-gray-200 transition-all"
            >
              Voltar
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const textLayers = Object.entries(layers).filter(([_, v]) => getLayerType(_, v) === 'text');
  const imageLayers = Object.entries(layers).filter(([_, v]) => getLayerType(_, v) === 'picture');

  // ============================================
  // RENDER - EDITOR
  // ============================================
  
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to={`/contents/batch/${batchId}`}
                className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-gray-600" />
              </Link>
              <div>
                <h1 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-amber-500" />
                  Editar Post
                </h1>
                {templateInfo?.title && (
                  <p className="text-sm text-gray-500">{templateInfo.title}</p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {editHistory.length > 0 && (
                <button
                  onClick={() => setShowHistory(true)}
                  className="p-2.5 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                  title="Histórico"
                >
                  <History className="w-5 h-5" />
                </button>
              )}
              
              <button
                onClick={handleSave}
                disabled={!hasChanges || isSaving}
                className={`inline-flex items-center gap-2 px-5 py-2.5 rounded-xl font-medium transition-all ${
                  hasChanges && !isSaving
                    ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                {isSaving ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : saveSuccess ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Save className="w-4 h-4" />
                )}
                {isSaving ? 'Salvando...' : saveSuccess ? 'Salvo!' : 'Salvar'}
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* Conteúdo */}
      <main className="max-w-7xl mx-auto px-4 py-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
          
          {/* Preview - 3 colunas */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden sticky top-20">
              <div className="px-5 py-4 border-b border-gray-100 flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Preview</span>
                </div>
                {canvasReady && (
                  <span className="text-xs text-green-600 bg-green-50 px-2 py-1 rounded-full">
                    ● Ao vivo
                  </span>
                )}
              </div>
              
              <div className="p-4">
                <div className="relative bg-gray-100 rounded-xl overflow-hidden">
                  <div 
                    ref={canvasRef}
                    id="placid-canvas"
                    className="w-full"
                    style={{ aspectRatio: '1/1', minHeight: '300px' }}
                  />
                  
                  {(!sdkReady || !canvasReady) && (
                    <div className="absolute inset-0 bg-white/90 backdrop-blur-sm flex items-center justify-center">
                      <div className="text-center">
                        <Loader2 className="w-8 h-8 text-amber-500 animate-spin mx-auto mb-2" />
                        <p className="text-sm text-gray-500">
                          {!sdkReady ? 'Carregando editor...' : 'Preparando preview...'}
                        </p>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
          
          {/* Formulário - 2 colunas */}
          <div className="lg:col-span-2 space-y-4">
            
            {/* Textos */}
            {textLayers.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <Type className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Textos</span>
                </div>
                
                <div className="p-5 space-y-4">
                  {textLayers.map(([layerName, layerValue]) => (
                    <div key={layerName}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formatLayerName(layerName)}
                      </label>
                      <textarea
                        value={layerValue?.text || ''}
                        onChange={(e) => handleTextChange(layerName, e.target.value)}
                        className="w-full px-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all resize-none text-gray-900"
                        rows={layerName.toLowerCase().includes('title') ? 2 : 1}
                        placeholder={`Digite aqui...`}
                      />
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Imagens */}
            {imageLayers.length > 0 && (
              <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
                <div className="px-5 py-4 border-b border-gray-100 flex items-center gap-2">
                  <ImageIcon className="w-4 h-4 text-amber-500" />
                  <span className="font-medium text-gray-900">Imagens</span>
                </div>
                
                <div className="p-5 space-y-5">
                  {imageLayers.map(([layerName, layerValue]) => (
                    <div key={layerName}>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        {formatLayerName(layerName)}
                      </label>
                      
                      <div className="flex gap-3">
                        {layerValue?.image && (
                          <div className="w-16 h-16 rounded-lg overflow-hidden border border-gray-200 flex-shrink-0">
                            <img
                              src={layerValue.image}
                              alt={layerName}
                              className="w-full h-full object-cover"
                            />
                          </div>
                        )}
                        
                        <div className="flex-1 flex gap-2">
                          <div className="flex-1 relative">
                            <LinkIcon className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                              type="url"
                              value={layerValue?.image || ''}
                              onChange={(e) => handleImageUrlChange(layerName, e.target.value)}
                              className="w-full pl-10 pr-4 py-3 bg-gray-50 border border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 transition-all text-sm"
                              placeholder="URL da imagem..."
                            />
                          </div>
                          
                          <button
                            onClick={() => {
                              const input = document.createElement('input');
                              input.type = 'file';
                              input.accept = 'image/jpeg,image/png,image/webp,image/gif';
                              input.onchange = (e) => {
                                const file = e.target.files?.[0];
                                if (file) handleImageUpload(layerName, file);
                              };
                              input.click();
                            }}
                            disabled={isUploading}
                            className="px-4 py-3 bg-gray-100 hover:bg-gray-200 rounded-xl transition-all flex-shrink-0"
                          >
                            {isUploading ? (
                              <Loader2 className="w-5 h-5 text-gray-600 animate-spin" />
                            ) : (
                              <Upload className="w-5 h-5 text-gray-600" />
                            )}
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            
            {/* Dica */}
            <div className="bg-amber-50 rounded-xl p-4 flex items-start gap-3">
              <Sparkles className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-amber-800">
                Edite os campos acima e veja as mudanças em tempo real. Clique em <strong>Salvar</strong> para gerar a imagem final.
              </p>
            </div>
            
          </div>
        </div>
      </main>

      {/* Modal Histórico */}
      {showHistory && (
        <div 
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          onClick={() => setShowHistory(false)}
        >
          <div 
            className="bg-white rounded-2xl shadow-xl max-w-lg w-full max-h-[80vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="px-5 py-4 border-b border-gray-200 flex items-center justify-between">
              <h2 className="font-semibold text-gray-900 flex items-center gap-2">
                <History className="w-5 h-5 text-amber-500" />
                Histórico
              </h2>
              <button
                onClick={() => setShowHistory(false)}
                className="p-2 hover:bg-gray-100 rounded-lg transition-all"
              >
                <X className="w-5 h-5 text-gray-500" />
              </button>
            </div>
            
            <div className="p-4 overflow-y-auto max-h-[60vh]">
              {editHistory.length === 0 ? (
                <p className="text-center text-gray-500 py-8">Nenhum histórico</p>
              ) : (
                <div className="space-y-3">
                  {editHistory.map((entry, index) => (
                    <div 
                      key={index}
                      className="flex items-center gap-4 p-3 bg-gray-50 rounded-xl"
                    >
                      {entry.previous_url && (
                        <img
                          src={entry.previous_url}
                          alt={`Versão ${index + 1}`}
                          className="w-14 h-14 object-cover rounded-lg"
                        />
                      )}
                      
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm">
                          Versão {editHistory.length - index}
                        </p>
                        <p className="text-xs text-gray-500">
                          {new Date(entry.edited_at).toLocaleString('pt-BR')}
                        </p>
                      </div>
                      
                      <button
                        onClick={() => handleRevert(index)}
                        disabled={isSaving}
                        className="px-3 py-2 text-sm font-medium text-amber-600 hover:bg-amber-50 rounded-lg transition-all flex items-center gap-1.5"
                      >
                        <RotateCcw className="w-4 h-4" />
                        Restaurar
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PlacidContentEditor;